import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useStore } from 'zustand';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApi } from '../../../lib/api';
import { ApiProblemError, WriteRefusedError, fetchJson } from '../../../lib/apiFetchJson';
import { useImpersonation } from '../../../hooks/useImpersonation';
import { useUserControlsContext } from '../../../contexts/UserControlsContext';
import { effectiveIdentityKey } from '../../../hooks/useSyncEffectiveIdentity';
import { useImpersonateWriteModeStore } from '../../../lib/impersonate-write-mode';
import { QueueResponseSchema, SendResponseSchema, EnrollmentItemSchema, StepDraftSchema, UNRESOLVED_PLAY_NAME, type QueueResponse, type EnrollmentItem, type StagedSendConfig } from '@shared/flow/enrollment';
import type { PlayOption } from '@shared/flow/play-catalog';
import type { GongFlowStepDetailWire } from '@shared/flow/gong-flow-catalog';
import { gongEngageUrl } from '../links';
import { useUnsavedChangesWarning } from '../../shared/useUnsavedChangesWarning';
import { EMAIL_STEP_TYPES } from './StepOverrideEditor';
import {
  createStepOverrideStore,
  hasAnyStepOverrideDraft,
  stepOverrideItemPrefix,
  stepOverrideKey,
  subscribeStepOverrideKey,
  clearStepOverrideDraftsForItem,
  restoreStepOverrideDraftsForItem,
  stepDraftReflectsAckedDraft,
  type ReadonlyStepOverrideStore,
  type StepOverrideDraft,
  type StepOverrideSnapshot,
} from './state/step-override-drafts';
import {
  buildStagedSendSnapshot,
  clearSenderTouched,
  markSenderTouched,
  playConfigKey,
  readStagedConfig,
  reconcileStagedPlayConfig,
  setStagedBaseCopy,
  setStagedBaseSender,
  stagedConfigHasHydrated,
  type PlayKey,
} from './state/staged-play-config';
import { deriveStagedStepWrites } from './bulk-step-defaults';
import { FLOW_GONG_STEPS_KEY, fetchGongFlowSteps } from './gong-flow-queries';
import {
  dropItemDraftReset,
  settleItemDraftReset,
  usePendingItemDraftResets,
} from './state/item-draft-reset';
import { contactDisplayIdentity } from './contactIdentity';
import { useEnrollQueueQueries } from './useEnrollQueueQueries';
import { useItemOpQueue } from './useItemOpQueue';
import { useSendLock } from './useSendLock';
import { useQueueUiSignals } from './useQueueUiSignals';
import { useBulkQueueMutations } from './state/use-bulk-queue-mutations';
import { useStagedMaterialization } from './state/use-staged-materialization';
import { CONTACT_STATE_INVALIDATION_KEYS } from './state/contact-state-invalidation-keys';
import { useItemAutosave, itemReflectsPatch, asFlushError, AUTOSAVE_DEBOUNCE_MS } from './useItemAutosave';
import { QUEUE_KEY } from './queue-query-keys';
import { FLOW_REQUESTS_KEY } from './requests-hooks';
import {
  PatchQueueItemError,
  type BaseCopyDraftStore,
  type EnrollQueueContextValue,
  type EnrollQueueTimers,
  type PendingSendItemSnapshot,
} from './enroll-queue-types';

/**
 * F9 god-context cut 1 of 4 — the type surface now lives in
 * `enroll-queue-types.ts`. Re-exported verbatim from here so every existing
 * `from './EnrollQueueContext'` import (`RailDock`'s `SidebarTab`,
 * `PlaysPane`'s `QueueFlashRequest`, index.ts's `EnrollQueueContextValue` /
 * `ContactJumpHandler`, …) keeps working untouched.
 */
export type {
  SidebarTab,
  ContactJumpRequest,
  ContactJumpHandler,
  BaseCopyDraftStore,
  EnrollQueueContextValue,
  // The provider's injectable wall-clock delays — re-exported here because
  // `EnrollQueueProvider` (this module) is what takes them as a prop, so a
  // caller typing a `timers` value imports it from the same place.
  EnrollQueueTimers,
} from './enroll-queue-types';

/**
 * Flow Queuing System (v0) — shell-level enroll queue context (U4).
 *
 * Mounted once above the mini-app router (`AuthenticatedLayout`, a sibling
 * of `<Outlet/>` — see docs/flow/flow-queuing-implementation-plan.md
 * "Shell mount point"). `WorkflowsDropdown` (rendered deep inside the
 * mini-app tree via `ContactActionMenus` — the single-contact enroll
 * surface shared by the contact card, sidebar search, and `ContactFlowTable`'s
 * row action, flow-contacts-bulk-send U7; the retired `EnrollButton` used to
 * carry this role alone) and the dock (`RailDock`, rendered at shell level)
 * both consume this context, so an add from any contact surface is
 * immediately visible in the docked sidebar without prop drilling.
 *
 * D4: enrolling is a write. Mirrors THE RULE in src/auth/write-guard.ts
 * (readOnly = impersonating && !writesUnlocked, same in every environment).
 * Defence in depth, not the enforcement point.
 *
 * D2 (single draft store): this context is the ONLY place `enrollment_item`
 * state is fetched/mutated on the client. The sidebar body, the
 * edit-in-place editor, and the full-screen review (U5) all read from
 * `groups` here and write through the single `patchItem` mutation below —
 * there is no separate per-surface copy or write path.
 */

const EnrollQueueContext = createContext<EnrollQueueContextValue | null>(null);

/**
 * The queue's React Query cache keys moved to `queue-query-keys.ts` (F9
 * god-context cut 2 of 4) so `useEnrollQueueQueries` (the readers) and this
 * file's mutations (the invalidators) can share them. `FLOW_QUEUE_PLAYS_KEY`
 * is re-exported (not imported) from here because Play Manager admin
 * (`components/admin/plays/PlaysPage.tsx`, via `EnrollQueue/index.ts`)
 * imports it off this module — no mutation in this file invalidates the plays
 * query any more, since D8 deleted the base-copy PUT that used to.
 */
export { FLOW_QUEUE_PLAYS_KEY } from './queue-query-keys';
/**
 * terra-ohe.7 (F1/DBOS durable-send migration U6) — the item-level autosave
 * machinery (the debounce constant, the tracked-write entry type, and the
 * `itemReflectsPatch`/`asFlushError` helpers) moved verbatim into
 * `useItemAutosave.ts`. Re-exported here so every pre-existing
 * `import { AUTOSAVE_DEBOUNCE_MS } from '../EnrollQueueContext'` (the test
 * suite's own import path) keeps working unchanged.
 */
export { AUTOSAVE_DEBOUNCE_MS } from './useItemAutosave';

/**
 * Re-maps an HTTP-status `ApiProblemError` (non-2xx) from `fetchJson` onto the
 * error type each queue mutation threw before it parsed through `fetchJson`
 * (`PatchQueueItemError` carries `.status`, which `selectInputOption` branches
 * on). Network / aborted / malformed failures pass through unchanged, and so
 * does a `WriteRefusedError`: its `userMessage` is what the rep must see.
 */
function mapHttpError(err: unknown, make: (e: ApiProblemError) => Error): unknown {
  if (err instanceof WriteRefusedError) return err;
  return err instanceof ApiProblemError && err.kind === 'http' ? make(err) : err;
}

/**
 * Typing-lag fix (2026-08-20) — the D3 beforeunload guard's subscription to
 * the step-override store's "any unsaved?" flag, isolated into its own leaf
 * component so a flag transition (false→true on the first edit of a
 * session, true→false once every draft is saved/reset) re-renders ONLY this
 * inert node — never `EnrollQueueProvider` itself. Mounted as a Provider
 * SIBLING of `{children}` (see the `return` at the bottom of
 * `EnrollQueueProvider`), not nested inside it, so it's fully outside the
 * `children` subtree React already bails on for unrelated provider
 * re-renders. Renders nothing; its only job is the `useUnsavedChangesWarning`
 * effect.
 *
 * The selector is the module-level `hasAnyStepOverrideDraft` (a stable
 * identity, so `useStore` never re-subscribes) and it returns a boolean, so
 * every draft write that does not FLIP the flag bails out of re-rendering
 * here — the same "only notify on a real transition" behavior the store's
 * hand-rolled predecessor spelled out by hand.
 */
function StepOverrideUnsavedGuard({ store }: { store: ReadonlyStepOverrideStore }) {
  const hasUnsaved = useStore(store, hasAnyStepOverrideDraft);
  useUnsavedChangesWarning(hasUnsaved);
  return null;
}

/**
 * Production timing — what `EnrollQueueProvider` uses for any `timers` key
 * the caller omits. `AppRouter.tsx` (the only non-test mount) passes no
 * `timers` at all, so it gets exactly these three values, unchanged from the
 * literals that used to be inline below.
 */
export const DEFAULT_ENROLL_QUEUE_TIMERS: EnrollQueueTimers = {
  autosaveDebounceMs: AUTOSAVE_DEBOUNCE_MS,
  sendPollInitialMs: 800,
  sendPollIntervalMs: 1200,
};

export function EnrollQueueProvider({
  children,
  timers,
}: {
  children: ReactNode;
  /**
   * Per-mount override for the provider's wall-clock delays (see
   * `EnrollQueueTimers`). Omitted everywhere in production; the queue's
   * real-composition test suites pass short values so they can exercise the
   * real debounce/poll code paths without the real waits.
   */
  timers?: Partial<EnrollQueueTimers>;
}) {
  // Destructured to three NUMBERS on purpose: callers pass `timers` as an
  // inline object literal, so the object identity changes every render but
  // these primitives do not — which keeps them safe to use as `useCallback`
  // deps below without churning the context value.
  const autosaveDebounceMs = timers?.autosaveDebounceMs ?? DEFAULT_ENROLL_QUEUE_TIMERS.autosaveDebounceMs;
  const sendPollInitialMs = timers?.sendPollInitialMs ?? DEFAULT_ENROLL_QUEUE_TIMERS.sendPollInitialMs;
  const sendPollIntervalMs = timers?.sendPollIntervalMs ?? DEFAULT_ENROLL_QUEUE_TIMERS.sendPollIntervalMs;
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const { active: impersonating, target: impersonationTarget } = useImpersonation();
  // Mirrors THE RULE in src/auth/write-guard.ts (readOnly = impersonating &&
  // !writesUnlocked, same in every environment). Defence in depth, not the
  // enforcement point.
  const writesUnlocked = useImpersonateWriteModeStore((s) => s.enabled);
  const readOnly = impersonating && !writesUnlocked;
  // Demo sessions mock Send server-side (the demo gate); every other write stays read-only.
  // `target` is null/undefined/'colleague' for real impersonation, which keeps Send disabled.
  const isDemo = impersonationTarget === 'demo';
  const sendReadOnly = readOnly && !isDemo;
  // A send is in flight (from POST until the delivery poll settles). Send
  // buttons disable on this (B1 client belt).
  const [isSending, setIsSending] = useState(false);
  // Flipped true on unmount so an in-flight poll loop stops re-scheduling
  // (M11: the poll otherwise survives navigation/sign-out and keeps hitting
  // the API + toasting every 1.2s).
  const cancelledRef = useRef(false);
  /**
   * terra-ohe.8 (F1/DBOS durable-send migration U6, Codex challenge, merged) —
   * the accepted-item ids the ACTIVE post-send poll (in `sendMutation`'s
   * `onSuccess` below) is still waiting to settle. `null` when no send is
   * currently being polled. `cancelMutation`'s `onSuccess` drops an id the
   * instant a rep cancels it, rather than waiting for the poll's next GET
   * /api/flow/queue round trip to notice the row left `queued` — the two
   * together are what let a batch settle immediately when one of its rows is
   * cancelled instead of hanging until the 30s+ deadline (a cancelled row
   * becomes `ready` with `last_send_error: null`, which the OLD partition,
   * keyed on `last_send_error` being truthy, could never tell apart from
   * "still pending").
   */
  const pendingSendIdsRef = useRef<Set<number> | null>(null);
  /**
   * D12 Codex round-2 fixes #3 and #4 — the per-item AUTOSAVE WRITE BARRIER:
   * item id -> how many operations currently hold it. While an item is
   * barred, `autosavePatch` refuses to schedule and `dispatchAutosave`
   * refuses to dispatch for it, so nothing this client queues during the
   * barred window can reach the server.
   *
   * Two operations claim it, both for the same reason — they are about to
   * make every outstanding local write for that item WRONG, and there is a
   * multi-await gap between deciding that and finishing:
   *
   *  - **Reset to base copy** (#3). `discardAutosave` awaits whatever write
   *    it observed AT CALL TIME and then deletes the entry. That is a drain,
   *    not a barrier: an edit made during the await was folded into the entry
   *    and then deleted with it (silently lost), and an edit made after the
   *    delete but before the reset's own PATCH resolved created a brand-new
   *    entry whose debounce fired AFTER the reset — re-applying exactly what
   *    Reset threw away.
   *  - **Reassignment** (#4). The server wipes the item's drafts on a real
   *    play change, so an old-play write landing after that PATCH resurrects
   *    play A's copy/sender under play B.
   *
   * A REFCOUNT rather than a `Set<number>`: the two claimants are reachable
   * concurrently for one item (a reset and a reassign are separate buttons),
   * and a boolean flag would let whichever finished first unbar the other
   * mid-flight. Held across awaits, so it lives in a ref (never state — no
   * consumer renders off it, and a re-render per claim would be pure churn).
   *
   * Deliberately NOT the same thing as `useSendLock`'s `busyItems`: that lock
   * is per-item send-pipeline mutual exclusion and is rendered (every surface
   * disables its fields on it). This one is purely a write gate, is invisible,
   * and — unlike the send lock — must NOT block the flush that deliberately
   * commits the rep's edit before a reassignment.
   */
  const writeBarriersRef = useRef<Map<number, number>>(new Map());
  const isItemWriteBarred = useCallback(
    (itemId: number) => (writeBarriersRef.current.get(itemId) ?? 0) > 0,
    [],
  );
  const beginItemWriteBarrier = useCallback((itemId: number) => {
    writeBarriersRef.current.set(itemId, (writeBarriersRef.current.get(itemId) ?? 0) + 1);
  }, []);
  const endItemWriteBarrier = useCallback((itemId: number) => {
    const next = (writeBarriersRef.current.get(itemId) ?? 0) - 1;
    if (next > 0) writeBarriersRef.current.set(itemId, next);
    else writeBarriersRef.current.delete(itemId);
  }, []);
  /**
   * Basic save-and-send (plan 2026-08-19-004, D1) — the LOCAL step-override
   * draft + per-step save-error store, keyed `${itemId}:${stepNumber}`.
   * Rendering derives `localDraft ?? persistedOverride ?? stepDefault`.
   *
   * Typing-lag fix (2026-08-20): this used to be React state (a `useState`
   * Map + a `useRef` mirror). It is now a zustand store
   * (`state/step-override-drafts.ts`) — ONE instance PER PROVIDER MOUNT,
   * never React state — so a keystroke's write never touches the giant
   * `value` memo below. Per-mount (not a module-level `create()` singleton,
   * and not `useRef(createStepOverrideStore())`, which would build a throwaway
   * store on every render) because this state is queue-session-scoped: a
   * singleton would leak drafts between independently-rendered
   * `EnrollQueueProvider`s, most visibly in tests where two providers live in
   * one file. `getState()` is always the up-to-date truth every writer and
   * `saveStepOverrides` read, exactly like the old ref mirror (so a late
   * caller holding an older render's closure — the review's per-row
   * `handleSend` runs AFTER the editor's own save already drained the item —
   * never re-saves from a stale snapshot). Entries are removed only by
   * save-success, reset-of-a-local-only edit, or item departure (ghost sweep
   * below) — never by a server refetch, which is what makes the derived-value
   * rule SSE-safe without `useDraftState`.
   */
  const [stepOverrideStore] = useState(() => createStepOverrideStore());
  /**
   * The rep's staged play-level base copy (design-fidelity plan D8 Part B) —
   * REP-LOCAL, BROWSER-PERSISTED client state, never server state until
   * Send. It lives in the module-scope `state/staged-play-config` zustand
   * store (localStorage via `persist`, keyed per effective identity INSIDE
   * the blob — see that module's doc comment for the identity keying, the
   * cross-tab rehydrate, and why it is deliberately NOT in
   * `lib/identity-scoped-reset`'s wipe registry), not in provider React
   * state: staged text must survive not just card collapse / rail switch /
   * grouping switch (the provider outlives those) but a full reload, tab
   * close and browser restart (Spencer, 2026-09-02).
   *
   * `IDENTITY` is the canonical effective-identity composition
   * `useSyncEffectiveIdentity.ts` uses, and it honors that hook's
   * resolved-identity guard: `null` while `/api/me` is unresolved (or during
   * a Clerk sessionId flap), which makes the facade read empty and its
   * writes no-op rather than staging under a wrong/anonymous key.
   */
  const { userId, impersonation } = useUserControlsContext();
  const stagedIdentity = effectiveIdentityKey(userId, impersonation);
  /**
   * Grouped read/write API for the staged base copy — ONE prop threads down
   * through `RailDock` → `PlaysPane` → the play rows →
   * `BaseCopyEditor`. `setDraft` is the entire write path
   * — no endpoint, no network (`persist` writes localStorage on each store
   * write).
   *
   * ── D24 (qa-round3 plan, fix 1): memoized on `[stagedIdentity]` ONLY ─────
   * This used to be `useMemo(..., [stagedConfig, stagedIdentity])` over a
   * `useStagedConfig(stagedIdentity)` subscription held HERE, in the
   * provider's own body. Every staged mutator mints a fresh bucket
   * (`updateBucket`), so that subscription re-rendered this 2400-line
   * provider on every base-copy KEYSTROKE, re-memoized this facade, and
   * re-minted the `value` memo below — which, because context propagation
   * bypasses React's `children`-identity bailout, re-rendered all 16
   * `useEnrollQueue()` consumers per character. That included
   * `StatePills`/`ContactActionMenus`, mounted PER CONTACT-TABLE ROW behind
   * the dock, each running an O(queue-size) `groups.some(...)` scan — ~5,000
   * comparisons per keystroke on a surface with nothing to do with base copy.
   *
   * Every field on this facade is a write; the consumers that actually
   * DISPLAY a staged value declare their own narrow subscription off
   * `identity` below — `BaseCopyEditor`, `PlayQueueCard` and
   * `ContactQueueRow` all use the per-field hooks in
   * `state/staged-play-config.ts`. Writes are unchanged: the same
   * module-level setters, called with the same identity.
   */
  const baseCopyDraftStore = useMemo<BaseCopyDraftStore>(() => ({
    identity: stagedIdentity,
    setDraft: (playTypeId: number, playInstanceRef: string, draft: { subject: string; body: string }) =>
      setStagedBaseCopy(stagedIdentity, playConfigKey(playTypeId, playInstanceRef), draft),
    // D12 — the SENDER half rides this same facade (same store, same
    // identity keying, same never-before-Send lifecycle), so exactly one
    // prop keeps threading through `RailDock` → `PlaysPane` →
    // the play rows → `BaseCopyEditor`.
    setBaseSender: (playTypeId: number, playInstanceRef: string, sender: string) =>
      setStagedBaseSender(stagedIdentity, playConfigKey(playTypeId, playInstanceRef), sender),
    markSenderTouched: (itemId: number) => markSenderTouched(stagedIdentity, itemId),
    clearSenderTouched: (itemId: number) => clearSenderTouched(stagedIdentity, itemId),
  }), [stagedIdentity]);

  // F9 god-context cut 3 of 4 — the per-item send-pipeline lock now lives in
  // `useSendLock`. Verified independent of the surrounding mutations code
  // (which stays in this file for the F1/DBOS worktree): the hook reads no
  // mutation and no query, and callers keep the exact same three members off
  // the context value. `busyItems` itself is still needed here by the
  // ghost-entry sweep below.
  const {
    busyItems,
    isItemBusy: isSendPipelineBusy,
    beginSendPipeline: claimSendPipeline,
    endSendPipeline,
  } = useSendLock();
  /**
   * D12 Codex round-3 fixes #1/#2/#3 — the per-item DESTRUCTIVE-OPERATION
   * SERIAL QUEUE. See `useItemOpQueue`'s module doc comment for the three
   * bugs it closes and why neither `useSendLock` nor `writeBarriersRef` could.
   * `resetItemToBase` and `assignPlay` are its only two producers; every
   * other member here composes with it rather than duplicating it.
   */
  const { runItemOp, isItemOpPending, awaitItemOps, opPendingItems } = useItemOpQueue();
  /**
   * D12 Codex round-3 fix #1, refusal half — Send must not be able to START
   * for an item whose reset/reassignment is still running.
   *
   * The refusal is a SYNCHRONOUS ref read (`isItemOpPending`), layered ahead
   * of `useSendLock`'s own check-and-set for exactly the reason that lock is
   * ref-based: a Send click landing in the same event batch as the op must be
   * refused, and a state read would be a render behind.
   *
   * This used to be what made a write-barrier-skip clause inside the (now
   * deleted, terra-ohe.7) client-side send-time materialization function
   * unreachable rather than load-bearing: the barrier is only ever held INSIDE a queued
   * op, so `barrier held ⇒ op pending ⇒ no send pipeline was allowed to claim
   * this item`. Round 2 had the skip without the refusal, so the pipeline
   * proceeded and POSTed the item with whatever the server currently held
   * instead of the staged value the UI was showing. The refusal itself is
   * still load-bearing independent of that history: it is what stops a Send
   * from racing a reset/reassignment's in-flight PATCH.
   */
  const beginSendPipeline = useCallback(
    (itemIds: number[]): boolean => {
      if (itemIds.some(id => isItemOpPending(id))) return false;
      return claimSendPipeline(itemIds);
    },
    [claimSendPipeline, isItemOpPending],
  );
  /**
   * D12 Codex round-3 fix #1, VISIBLE half — an item mid-reset or
   * mid-reassignment now reports busy to every surface, so its fields lock
   * and its Send/Reset/Send-anyway affordances disable exactly as they do
   * during a send pipeline. Round 2's write barrier was invisible, which is
   * why the Send button stayed clickable through a reset.
   *
   * Derived (never a third piece of state) from the two underlying sets, so
   * there is still exactly ONE thing every consumer asks: "can I touch this
   * row right now?"
   */
  const isItemBusy = useCallback(
    (itemId: number) => isSendPipelineBusy(itemId) || opPendingItems.has(itemId),
    [isSendPipelineBusy, opPendingItems],
  );
  // Reset on mount, flip true on unmount. The mount reset is load-bearing under
  // React StrictMode: dev double-invoke runs setup→cleanup→setup, so a
  // cleanup-only effect would leave cancelledRef.current permanently `true`
  // after the initial mount, making every send poll early-exit (line ~340) and
  // orphaning the "Sending…" toast forever. Resetting here keeps it correct
  // through the double-invoke AND real remounts (navigation back into the queue).
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  // F9 god-context cut 2 of 4 — every READ (queue/requests/plays/suggested-
  // Gong-flows/senders/input-options) plus the derived state built straight
  // off them now lives in `useEnrollQueueQueries`. The mutations below are
  // deliberately NOT part of that cut (reserved for the F1/DBOS worktree);
  // they invalidate through the shared keys in `queue-query-keys.ts`.
  const {
    queueData,
    groups,
    plays,
    activePlayTypes,
    gongFlowsSuggested,
    sendersFor,
    defaultSenderFor,
    inputOptionsFor,
    totalCount,
    isLoading,
    playsResolved,
  } = useEnrollQueueQueries();

  /**
   * U-C — centralized contact-state cache invalidation. Every mutation that
   * changes an `enrollment_items` row in a way a contact's "Queued" pill/
   * fact-card must reflect calls this ONE helper, replacing what used to be
   * ad-hoc `invalidateQueries` calls scattered across consumers (e.g.
   * StatePills.tsx's `AddToQueueButton`, which invalidated these same two
   * keys locally — removed in this same change so there is exactly one
   * place this logic lives). The exact key list lives in
   * `CONTACT_STATE_INVALIDATION_KEYS`
   * (`state/contact-state-invalidation-keys.ts`) — see that module's doc
   * comment for what each root covers and why (contact-state, contact-state
   * detail, contact/account signals, `FLOW_PLAYS_QUERY_KEY`, and the
   * `useFlowContacts` list root — which, since plan 2026-09-25-002 U7, is
   * also what refreshes the Plays tab's three "People in <Play>" sections).
   *
   * Wired into: addMutation.onSuccess, patchMutation.onSuccess,
   * removeMutation.onSuccess, and the two SEND-SETTLE points inside
   * sendMutation's onSuccess poll loop below (the success branch, where an
   * accepted item flips to `submitted` and drops off the active queue —
   * the Queued pill/fact must disappear, and the write-through send may add
   * a new `invited` fact; and the deadline-expiry branch, which covers a
   * PARTIAL settle — some items sent, others still `ready` — so contact
   * state must refresh even though not everything finished). Deliberately
   * NOT wired to sendMutation.onError: a failed send (the POST itself
   * rejecting) leaves every item's status exactly where it was — nothing
   * about contact state changed, so there's nothing to invalidate.
   */
  /**
   * Debounced (round-4 follow-up, Spencer — bulk-send-at-scale QA): every
   * `invalidateQueries` call below is UNSCOPED (no specific contact id), so
   * each one refetches EVERY currently-mounted query in that family — every
   * loaded Contacts-table page chunk (`FLOW_CONTACT_STATE_KEY`) and every
   * open ContactDetail panel (`FLOW_CONTACT_STATE_DETAIL_KEY`). That's fine
   * for a single edit, but `patchMutation.onSuccess` below calls this once
   * per successful PATCH — a 100-item bulk send's per-item autosave flush
   * fires ~100 of these in a burst, each re-triggering every mounted
   * chunk/detail query, which is exactly the request-storm shape that hit
   * Chrome's `ERR_INSUFFICIENT_RESOURCES` ceiling during a 100-contact test.
   * A short trailing debounce collapses a burst into ONE real invalidation
   * fired after the burst settles — correctness is unchanged (this is
   * "invalidate on mutation", not a TTL; it still always fires, just once
   * per burst instead of once per item).
   */
  const invalidateContactStateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const invalidateContactState = useCallback(() => {
    if (invalidateContactStateTimer.current !== null) clearTimeout(invalidateContactStateTimer.current);
    invalidateContactStateTimer.current = setTimeout(() => {
      invalidateContactStateTimer.current = null;
      for (const key of CONTACT_STATE_INVALIDATION_KEYS) void queryClient.invalidateQueries({ queryKey: key });
    }, 300);
  }, [queryClient]);

  const addMutation = useMutation({
    mutationFn: async (input: {
      contactId: string;
      sourceContext?: string;
      note?: string;
      playTypeId?: number;
      playInstanceRef?: string;
      warmIntroId?: string;
    }) => {
      return fetchJson(apiFetch, EnrollmentItemSchema, '/api/flow/queue/items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Wire body uses the QueueAddSchema field names (snake_case for the
        // play fields) — only sent when the caller actually passed them, so
        // the pre-existing add-unassigned contract is unchanged byte-for-byte.
        body: JSON.stringify({
          contactId: input.contactId,
          ...(input.sourceContext !== undefined ? { sourceContext: input.sourceContext } : {}),
          ...(input.note !== undefined ? { note: input.note } : {}),
          ...(input.playTypeId !== undefined ? { play_type_id: input.playTypeId } : {}),
          ...(input.playInstanceRef !== undefined ? { play_instance_ref: input.playInstanceRef } : {}),
          ...(input.warmIntroId !== undefined ? { warm_intro_id: input.warmIntroId } : {}),
        }),
      }).catch((err: unknown) => {
      // U12: surface the SERVER's own `problem()` detail/title (the same
      // treatment `patchMutation` below already gives its errors) instead of
      // a bare status code. RequestDetail's Re-enroll renders this string
      // inline to the rep, and "Failed to add to queue (409)" tells them
      // nothing about WHY. Callers that only toast a generic message
      // (ContactActionMenus, BulkEnrollPopover) are unaffected — they
      // already ignore `err.message`.
      throw mapHttpError(err, e => new Error(e.userMessage ?? 'Could not add this contact to your queue.'));
      });
    },
    // Consume-before-mount race fix (2026-08-22, "Browse all Gong flows"
    // one-click bug): AWAIT the queue invalidation rather than firing it
    // and forgetting it. `addContact` below calls `addMutation.mutateAsync`
    // and its own returned promise doesn't resolve until `onSuccess`
    // resolves — react-query awaits an `onSuccess` that returns a promise
    // before settling `mutateAsync`'s own promise. A caller that awaits
    // `addContact(...)` and then immediately acts on "the queue now has
    // this contact" (`ContactActionMenus.tsx`'s `handleBrowseAll`, which
    // fires `requestQueueFlash` + `openQueueTab()` right after) used to race
    // a refetch that hadn't landed in the cache yet — the flash/auto-open
    // request could fire (and, without QueueBody.tsx's matching fix, get
    // consumed) before the new row even existed in `groups`.
    // `invalidateContactState()` stays fire-and-forget — its ordering is
    // unrelated to this fix.
    onSuccess: async () => {
      invalidateContactState();
      await queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
    },
  });

  const patchMutation = useMutation({
    mutationFn: async (input: { itemId: number; patch: Record<string, unknown> }) => {
      return fetchJson(apiFetch, EnrollmentItemSchema, `/api/flow/queue/items/${input.itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input.patch),
      }).catch((err: unknown) => {
        // U2 (buttonless autosaving editor): surface the SERVER's own message
        // verbatim (InvalidPatchError/SenderRequiredError's `err.message`,
        // returned as the `problem()` body's `title` — see src/http/
        // responder.ts) rather than a generic "Failed to update queue item
        // (400)" string. `EnrollmentItemEditor`'s inline 400 render (a
        // rejected mark-ready transition) depends on this being the real
        // reason, not a status code. Falls back to the generic message if the
        // body isn't JSON or has no `title` (a non-`problem()` failure, e.g. a
        // proxy error page).
        throw mapHttpError(
          err,
          e => new PatchQueueItemError(e.status, e.userMessage ?? e.title ?? 'Could not update this queue item.'),
        );
      });
    },
    // Codex adversarial follow-up (D9 regression #2) — a successful patch on
    // an item is the SAME generalized seam every non-send fix action already
    // funnels through: `assignPlay` ("Change Play"), `handleSendAnyway`
    // (persists `recent_invite_override`), `selectInputOption` (the
    // connector/warm-intro picker), `handleToggleCoolOff`, and the plain
    // sender/subject/body autosave PATCH all call THIS mutation. The LAST
    // send response's server-authoritative skip label
    // (`sendSkipReasons`/`sendSkipLabelFor`, U4 behavior spec 5) used to be
    // replaced only by another send response — never cleared by any of
    // these fixes — so a stale "will skip — <reason>" label (and a
    // disabled row Send) could survive right past the rep actually fixing
    // it, with no send round trip in between. Clearing this item's entry
    // here (`clearSendSkipLabel`, `useQueueUiSignals.ts`) — on ANY
    // successful patch, not just a targeted one — is safe:
    // `deriveGroupSendability`'s `groupBlockLabel` falls back to the
    // client-derived `itemBlockReason` the instant the server reason is
    // gone, and that check independently re-verifies the conditions it CAN
    // see (sender, play, recent-send flag) from the item's current fields —
    // so a genuinely still-blocked item stays blocked, it just shows the
    // client's own reason until the next real send response.
    //
    // terra-ohe.7 (F1/DBOS durable-send migration U6) — the ONE caller that
    // used to opt out (`suppressSkipLabelClear`, set only by the deleted
    // client-side materialization-rollback PATCH) no longer exists: the
    // server resolves the staged trio itself (U2), so there is no
    // compensating-rollback write left to distinguish from a rep's own fix.
    // Every successful patch clears the label unconditionally now.
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
      invalidateContactState();
      clearSendSkipLabel(variables.itemId);
    },
  });

  /**
   * D23 (flow-contacts-bulk-send QA round 3, item 11) — two fixes on top of
   * the pre-existing shape:
   *  - Throws the status-carrying `PatchQueueItemError` (matching
   *    `unstickMutation` below), not a plain `Error` — `removeItem`
   *    (`useQueueUiSignals.ts`) needs `.status` to tell an EXPECTED 404 (the
   *    row is already gone — a benign double-fire, or something else
   *    removed it) apart from a genuinely unexpected failure.
   *  - AWAITS the queue invalidation, matching `addMutation`/`bulkAddMutation`
   *    (`:710-713`/`:882-885`) — this used to be `void
   *    queryClient.invalidateQueries(...)`, fire-and-forget, which left the
   *    row mounted (and its remove control re-enabled the instant
   *    `runItemOp` released) for the WHOLE refetch round trip. That window
   *    is exactly where a second click's DELETE could land and 404.
   *  - `meta: { suppressErrorToast: true }` — Codex adversarial review
   *    (round 3 batch) caught that without this, the global
   *    `MutationCache.onError` (`main.tsx`, `mutation-cache-handler.ts`)
   *    toasts "Failed to remove queue item (404)" regardless of `removeItem`
   *    (`useQueueUiSignals.ts`) catching the rejection locally and choosing
   *    to stay silent on a benign double-fire — the cache-level `onError`
   *    fires independently of whether the caller catches `mutateAsync`'s
   *    promise. `removeItem` owns 100% of this mutation's error
   *    presentation (silent on 404, a real toast on anything else), so the
   *    global path must be suppressed, not just locally out-raced.
   */
  const removeMutation = useMutation({
    mutationFn: async (itemId: number) => {
      const res = await apiFetch(`/api/flow/queue/items/${itemId}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) throw new PatchQueueItemError(res.status, `Failed to remove queue item (${res.status})`);
    },
    onSuccess: async () => {
      invalidateContactState();
      await queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
    },
    meta: { suppressErrorToast: true },
  });

  /**
   * POST /api/flow/queue/items/:id/unstick — PM1 (PR #1312 pre-merge fix):
   * the "Reset to retry" action on a reaper-flagged stuck row. `onSettled`,
   * not `onSuccess` (unlike `removeMutation` above): a 400 here means the
   * item already moved on (send completed, or already reset) between the
   * rep seeing the stuck label and clicking the button — a self-resolving
   * race, not a real error. Either way the `QUEUE_KEY` refetch repaints the
   * row's true state, so there is no bespoke error UI to show. No
   * `invalidateContactState()` — the item never left the queue, so no
   * contact-state pill reads anything this changes.
   */
  const unstickMutation = useMutation({
    mutationFn: async (itemId: number) => {
      const res = await apiFetch(`/api/flow/queue/items/${itemId}/unstick`, { method: 'POST' });
      // Status-carrying error (PatchQueueItemError's shape, above) — `unstickItem`
      // below needs `.status` to tell the EXPECTED race (400, `ItemNotStuckError`:
      // the item already moved on between the stuck label rendering and the
      // click) apart from a genuinely unexpected failure (network error, 500).
      if (!res.ok && res.status !== 204) throw new PatchQueueItemError(res.status, `Failed to reset queue item (${res.status})`);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
    },
  });

  /**
   * POST /api/flow/queue/items/:id/cancel — terra-ohe.8 (F1/DBOS durable-send
   * migration U6): a rep's explicit cancel of a `queued` row, before the DBOS
   * workflow claims it. Sibling of `unstickMutation` above, but unlike its
   * `onSettled` (a 400 there is a self-resolving race with nothing to tell the
   * rep), a cancel 400 (`ItemNotCancelableError` — the row already moved to
   * `sending` between the rep seeing "Cancel" and clicking it, or was already
   * resolved) IS worth surfacing: the rep asked to stop something that may
   * already be too late to stop. Message extraction mirrors `patchMutation`
   * above — the server's own `problem()` body `title` (`ItemNotCancelableError
   * .message`), not a generic status-code string — so the toast says exactly
   * why. `meta.suppressErrorToast` opts out of the global MutationCache toast
   * (which can't see the real title — `PatchQueueItemError` isn't a
   * `FetchError`, so `notifyError` falls back to generic copy for it): the
   * `cancelQueuedItem` wrapper below owns 100% of this mutation's error
   * presentation, same posture as `removeMutation`.
   */
  const cancelMutation = useMutation({
    mutationFn: async (itemId: number) => {
      // Behavior Spec 11 — the route returns the FULL updated wire item on
      // success (200, not 204 like unstick). Validated for shape-safety even
      // though this mutation only needs the itemId (already the mutation's
      // own variable) to update `pendingSendIdsRef` below — `QUEUE_KEY` itself
      // is repainted by the invalidation, not by splicing this response in.
      return fetchJson(apiFetch, EnrollmentItemSchema, `/api/flow/queue/items/${itemId}/cancel`, { method: 'POST' }).catch(
        (err: unknown) => {
          throw mapHttpError(
            err,
            e => new PatchQueueItemError(e.status, e.title ?? `Failed to cancel queue item (${e.status})`),
          );
        },
      );
    },
    onSuccess: (_item, itemId) => {
      pendingSendIdsRef.current?.delete(itemId);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
    },
    meta: { suppressErrorToast: true },
  });

  /**
   * `PUT /api/flow/queue/items/:id/steps/:n` — called ONLY from
   * `saveStepOverrides` (Send is the one write moment; no background PUT
   * exists). The invalidation is what lands the saved override on
   * `EnrollmentItem.step_drafts` for every other mounted surface.
   */
  const stepDraftMutation = useMutation({
    mutationFn: async (input: { itemId: number; stepNumber: number; stepType: string; patch: { subject_draft?: string; body_draft?: string } }) => {
      return fetchJson(apiFetch, StepDraftSchema, `/api/flow/queue/items/${input.itemId}/steps/${input.stepNumber}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step_type: input.stepType, ...input.patch }),
      }).catch((err: unknown) => {
        throw mapHttpError(err, e => new Error(`Failed to save step draft (${e.status})`));
      });
    },
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: QUEUE_KEY }); },
  });

  /**
   * `DELETE /api/flow/queue/items/:id/steps/:n` ("Use flow default"), called
   * ONLY from `saveStepOverrides` — the reset is local intent until Send
   * (Decision #4), which structurally closes P1-DEL: the DELETE only ever
   * runs inside the awaited, lock-covered save phase. The server's
   * `deleteMany` is idempotent, so a redundant DELETE is safe by contract.
   */
  const clearStepDraftMutation = useMutation({
    mutationFn: async (input: { itemId: number; stepNumber: number }) => {
      const res = await apiFetch(`/api/flow/queue/items/${input.itemId}/steps/${input.stepNumber}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) throw new Error(`Failed to clear step draft (${res.status})`);
    },
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: QUEUE_KEY }); },
  });

  const sendMutation = useMutation({
    // terra-ohe.7 (F1/DBOS durable-send migration U6) — `staged` is the
    // optional wire snapshot built by `buildStagedSnapshot` below
    // (`state/staged-play-config.ts`'s `buildStagedSendSnapshot`). Threaded
    // straight into the POST body; `JSON.stringify` drops an `undefined`
    // property, so an omitted `staged` posts byte-identically to the
    // pre-U6 body (`{ itemIds }` only) — old behavior for a batch with
    // nothing staged is unchanged.
    mutationFn: async (input: { itemIds: number[]; staged?: StagedSendConfig }) => {
      return fetchJson(apiFetch, SendResponseSchema, '/api/flow/queue/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemIds: input.itemIds, staged: input.staged }),
      }).catch((err: unknown) => {
        throw mapHttpError(err, e => new Error(`Failed to send (${e.status})`));
      });
    },
    onMutate: ({ itemIds }): Map<number, PendingSendItemSnapshot> => {
      setIsSending(true);
      // Snapshot every requested item's display info from the CURRENT cache
      // (not render-scope `groups`/`plays`, which could be stale by the time
      // this callback fires on a later click) — see the doc comment above.
      const snapshot = new Map<number, PendingSendItemSnapshot>();
      const queue = queryClient.getQueryData<QueueResponse>(QUEUE_KEY);
      // U4B (F4, catalog payload trim) — reads straight off each group's
      // own `play_name`/`ui` (queue.ts's `resolveGroupPlayInfo`, bounded by
      // queue size), not a `plays.find(...)` scan against the (now-trimmed,
      // for `catalog: true` kinds like gong_flow) `PlayCatalogResponse` —
      // that scan would silently come up empty for exactly the groups that
      // most need it. `group.play_name` already carries the server's own
      // `UNRESOLVED_PLAY_NAME` fallback (`@shared/flow/enrollment`) for a
      // play no longer in the live catalog (behavior spec 7) — the `??`
      // below is defensive type-narrowing only, matching that SAME shared
      // constant rather than recreating a raw-id string of its own, for an
      // unassigned item whose group never carries a `play_name` at all.
      for (const group of queue?.groups ?? []) {
        for (const item of group.items) {
          if (!itemIds.includes(item.id)) continue;
          const playLabel = group.play_name ?? (item.play_instance_ref ? UNRESOLVED_PLAY_NAME : null);
          snapshot.set(item.id, {
            contactId: item.contact_id,
            contactEmail: item.contact_email,
            displayName: contactDisplayIdentity(item),
            playName: playLabel,
            accountId: item.account_id,
            hasStepOverrides: group.ui?.has_step_overrides ?? false,
          });
        }
      }
      return snapshot;
    },
    onError: () => { setIsSending(false); },
    onSuccess: (data, _itemIds, snapshot) => {
      const acceptedIds = data?.accepted ?? [];
      const skipped = data?.skipped ?? [];
      // U4 behavior spec 5 — replace wholesale (see the state's doc comment).
      setSendSkipReasons(new Map(skipped.map(s => [s.item_id, s.reason])));
      void queryClient.invalidateQueries({ queryKey: QUEUE_KEY });

      const settle = () => {
        // terra-ohe.8 — release the tracked batch the instant this poll
        // stops (success, timeout, or unmount) so a LATER cancel (once this
        // send has already settled) has nothing stale left to delete from.
        pendingSendIdsRef.current = null;
        setIsSending(false);
      };

      /**
       * This bead (U5) — the ONE post-send toast, replacing the removed
       * auto-open-on-send navigation. Copy/semantics match the locked
       * prototype's `sendItems()`/`toast()` exactly
       * (docs/flow/gong-flow-play-and-queue-redesign-prototype.html:794-799):
       * a clean single success gets "Sent — <name> → <play>"; every other
       * combination (any failure/skip, or a multi-item send) gets the
       * "N sent · N failed · N skipped" bit-joined summary (omitting any
       * zero part), error-styled whenever anything FAILED — skips alone
       * don't turn it red, matching the prototype's `failed>0` flag exactly.
       * "View in Actions" — present whenever at least one item actually sent
       * — is the ONLY navigation this flow ever does, and it's
       * user-initiated by definition: it closes the review overlay if open,
       * then switches the docked sidebar to the Requests rail (never a
       * specific execution's detail view — the removed `openSentRequestDetail`
       * call drilled into one; this bead's copy doesn't).
       */
      const finalizeSend = (sentIds: number[], failedCount: number, toastId?: string | number) => {
        const sentCount = sentIds.length;
        const skippedCount = skipped.length;
        if (sentCount === 0 && failedCount === 0 && skippedCount === 0) {
          // Nothing to report (e.g. every accepted item was cancelled mid-send)
          // — still clear the loading toast so it never hangs.
          if (toastId !== undefined) toast.dismiss(toastId);
          return;
        }
        const isErr = failedCount > 0;
        const viewInActions = sentCount > 0
          ? { label: 'View in Actions', onClick: () => { setIsOpen(true); setActiveTab('requests'); } }
          : undefined;
        const notify = isErr ? toast.error : toast.success;
        if (sentCount === 1 && failedCount === 0 && skippedCount === 0) {
          const info = snapshot.get(sentIds[0]);
          /**
           * "View in Gong" toast follow-up (2026-08-20, Spencer decision
           * after a real successful live Gong send) — a clean single-item
           * success on a step-override-capable play (`ui.has_step_overrides`,
           * U5B — was a `play_type_slug === 'gong_flow'` comparison) routes
           * the toast action straight into that contact's Gong Engage
           * to-dos instead of the generic Actions rail switch. `window.open`
           * (not client-side navigation) is deliberate: this is an external
           * Gong URL, not an in-app route. Every OTHER outcome (multi-item
           * sends, any failure/skip, a play with no step overrides, or a
           * missing snapshot/contactId) keeps "View in Actions" unchanged.
           * KNOWN LIMITATION (flagged, not fixed here): `hasStepOverrides`
           * is true only for `gong_flow` today, so this is a correct
           * stand-in for "is this a Gong flow"; if a future second play
           * type sets `hasStepOverrides: true` without being Gong-backed,
           * this branch would incorrectly try to open a Gong URL for it —
           * that would need a dedicated capability (e.g. an external
           * deep-link field) rather than reusing this one. Out of scope for
           * this bead (only 4 `ui` capability fields exist today; see
           * `PlayConnectorSpec.ui`).
           */
          const action = info && info.hasStepOverrides && info.contactId
            ? {
                label: 'View in Gong ↗',
                onClick: () => { window.open(gongEngageUrl(info.contactId, info.accountId), '_blank', 'noopener,noreferrer'); },
              }
            : viewInActions;
          const options = { action, ...(toastId !== undefined ? { id: toastId } : {}) };
          notify(`Sent — ${info?.displayName ?? 'Contact'} → ${info?.playName ?? 'play'}`, options);
          return;
        }
        const bits: string[] = [];
        if (sentCount) bits.push(`${sentCount} sent`);
        if (failedCount) bits.push(`${failedCount} failed`);
        if (skippedCount) bits.push(`${skippedCount} skipped`);
        const options = { action: viewInActions, ...(toastId !== undefined ? { id: toastId } : {}) };
        notify(bits.join(' · '), options);
      };

      if (acceptedIds.length === 0) {
        // Nothing admitted — every requested item was skipped at admission
        // time, so there's nothing to poll for.
        finalizeSend([], 0);
        settle();
        return;
      }

      // The 202 only means ADMITTED — the actual sends run on `terra-worker`
      // (`FlowSendWorkflow`, paced 500ms/item). Don't claim "sent"
      // yet: show a loading toast and resolve it by polling until every
      // accepted item has settled — either it left the active queue
      // (submitted = sent) or it's back `ready` WITH `last_send_error` set
      // (= failed; it stays re-admittable, still in the queue). An item
      // still `ready` with no error after the deadline never confirmed
      // either way — surface that as a timeout rather than a false success.
      const n = acceptedIds.length;
      const noun = `contact${n === 1 ? '' : 's'}`;
      const toastId = toast.loading(`Sending ${n} ${noun}…`);
      const deadline = Date.now() + 30_000 + n * 600;
      // terra-ohe.8 — the MUTABLE twin of `acceptedIds` this poll actually
      // partitions against. `acceptedIds` itself stays frozen (it drives the
      // toast copy/deadline math above, which must not shrink mid-send just
      // because a row got cancelled) — `pendingSendIdsRef` is what
      // `cancelMutation.onSuccess` reaches into.
      pendingSendIdsRef.current = new Set(acceptedIds);
      const poll = async () => {
        // M11: stop entirely if the provider unmounted (navigation/sign-out)
        // — no more API hits, no more toasts, no re-schedule.
        if (cancelledRef.current) { settle(); return; }
        // M11: the deadline is checked FIRST, unconditionally. Previously it
        // lived only inside the `res.ok` and `catch` branches, so a non-OK
        // response (401 after sign-out, 5xx) fell straight through to the
        // re-schedule and polled forever.
        if (Date.now() > deadline) {
          toast.error(`${n} ${noun} didn't confirm sending — still in your queue`, { id: toastId });
          // U-C: a deadline expiry can still be a PARTIAL settle (some
          // accepted items reached `submitted` before the deadline, others
          // are still `ready`) — contact state must refresh even though the
          // send as a whole didn't fully confirm.
          invalidateContactState();
          settle();
          return;
        }
        try {
          const queue = await fetchJson(apiFetch, QueueResponseSchema, '/api/flow/queue');
          const byId = new Map(queue.groups.flatMap(g => g.items).map(it => [it.id, it] as const));
          // Perf LOW: we already have fresh queue data — write it straight
          // into the cache instead of invalidating (which would trigger a
          // SECOND fetch of the same endpoint every 1.2s).
          queryClient.setQueryData(QUEUE_KEY, queue);
          // U5 behavior spec 3/5 — partition every STILL-TRACKED accepted
          // id (`pendingSendIdsRef`, not the frozen `acceptedIds` — a
          // cancelled id is removed from the ref by `cancelMutation`'s
          // `onSuccess`, see that mutation's doc comment) into sent (left
          // the active queue = submitted), failed (present, reverted to
          // `ready` WITH `last_send_error`), or still pending (present AND
          // still `queued`/`sending` — the only two "not yet settled"
          // statuses since terra-ohe.8 widened admission to claim straight
          // to `queued`).
          //
          // Codex challenge (merged) — the settle check is now `status NOT
          // IN (queued, sending)`, not `status === ready && last_send_error`:
          // a row a rep just cancelled settles to `ready` with
          // `last_send_error: null`, which the OLD truthy-error check could
          // never distinguish from "still pending" and would hang on until
          // the 30s+ deadline. A row that left queued/sending with NO error
          // (cancelled, or any future non-error exit) is simply dropped from
          // the tally — neither sent nor failed, nothing to report for it.
          const sentIds: number[] = [];
          let failedCount = 0;
          let pendingCount = 0;
          const trackedIds = pendingSendIdsRef.current ?? new Set(acceptedIds);
          for (const id of trackedIds) {
            const item = byId.get(id);
            if (!item) { sentIds.push(id); trackedIds.delete(id); continue; }
            if (item.status === 'queued' || item.status === 'sending') { pendingCount++; continue; }
            if (item.status === 'ready' && item.last_send_error) failedCount++;
            trackedIds.delete(id);
          }
          if (pendingCount === 0) {
            finalizeSend(sentIds, failedCount, toastId);
            // U-C: at least SOME accepted item just flipped to `submitted`
            // and dropped off the active queue whenever sentIds is
            // non-empty — the Queued pill/fact must disappear, and the
            // write-through send may have added a new `invited` fact.
            // A send that's ENTIRELY failures changes nothing about
            // contact state, so skip the invalidation in that case (same
            // "nothing changed" rationale as `onError` above).
            if (sentIds.length > 0) {
              invalidateContactState();
              // The Requests feed normally refreshes off the live
              // `execution_status_changed` SSE event. A demo stream carries
              // pings only, and a real stream can miss a frame, so refresh
              // it here too: "View in Actions" must not land on a stale list.
              void queryClient.invalidateQueries({ queryKey: FLOW_REQUESTS_KEY });
            }
            settle();
            return;
          }
          // Non-OK / malformed: fetchJson throws into the catch below; fall through to re-schedule.
        } catch {
          // transient poll failure — fall through; deadline check ends it.
        }
        setTimeout(() => { void poll(); }, sendPollIntervalMs);
      };
      setTimeout(() => { void poll(); }, sendPollInitialMs);
    },
  });

  const addContact = useCallback(
    async (
      contactId: string,
      opts?: {
        sourceContext?: string;
        note?: string;
        playTypeId?: number;
        playInstanceRef?: string;
        warmIntroId?: string;
      },
    ) => {
      await addMutation.mutateAsync({ contactId, ...opts });
    },
    [addMutation],
  );

  /**
   * terra-ohe.7 (F1/DBOS durable-send migration U6) — the item-level autosave
   * machinery (`patchItem`/`dispatchAutosave`/`autosavePatch`/
   * `discardAutosave`/`flushAutosave`, plus the entry map they share) moved
   * verbatim into `useItemAutosave.ts` — the last reserved piece of this
   * 1,995-line context, per the in-source reservation at the top of this
   * file and `useItemAutosave.ts`'s own module doc comment. `autosaveEntriesRef`
   * is returned by the hook (not re-declared here) so the ghost-sweep effect
   * and the unmount-cleanup effect below keep reading/writing the SAME map.
   */
  const { patchItem, autosavePatch, flushAutosave, discardAutosave, autosaveEntriesRef } = useItemAutosave({
    patchMutation,
    isItemWriteBarred,
    awaitItemOps,
    autosaveDebounceMs,
  });
  // U2: a real unmount (not the StrictMode dev double-invoke, which re-runs
  // setup right after) drops any still-pending autosave timers — this
  // Provider is mounted once at shell level for the app's lifetime, so this
  // only fires on sign-out/teardown, never mid-session. In-flight promises
  // can't be cancelled; their settle handlers touching an unmounted
  // provider's state is harmless (teardown-only path). Split out from the
  // `cancelledRef` reset effect above (terra-ohe.7) because
  // `autosaveEntriesRef` now comes from `useItemAutosave`, called after that
  // earlier effect is declared.
  useEffect(() => {
    // eslint-plugin-react-hooks(exhaustive-deps) — "ref value accessed
    // directly in the effect cleanup" — flags `.current` reads inside the
    // returned closure. This ref holds a Map created once inside
    // `useItemAutosave` and never reassigned, so capturing `.current` HERE
    // (mount time) and closing over the same local in cleanup is exactly
    // equivalent to reading `.current` at cleanup time.
    const autosaveEntries = autosaveEntriesRef.current;
    return () => {
      for (const entry of autosaveEntries.values()) { if (entry.timer !== null) clearTimeout(entry.timer); }
      autosaveEntries.clear();
    };
  }, [autosaveEntriesRef]);

  /**
   * "Which play does the queue cache currently say this item is on?" — the
   * captured-context check both destructive operations below re-validate
   * against (D12 Codex round-3 fixes #2/#3).
   *
   * Read from the CACHE rather than render-scope `groups`, for the reason
   * `sendMutation.onMutate` and `assignPlay` already document: `groups` is a
   * render closure and can be stale by the time an async continuation runs —
   * and after waiting a turn in the op queue it definitely is. Returns
   * `undefined` for an item the cache does not know, which each caller
   * interprets for itself.
   */
  const cachedItem = useCallback(
    (itemId: number): EnrollmentItem | undefined => {
      const cached = queryClient.getQueryData<QueueResponse>(QUEUE_KEY);
      return cached?.groups.flatMap(g => g.items).find(it => it.id === itemId);
    },
    [queryClient],
  );

  // F9 god-context cut 5 — `resetItemToBase` (plus the `samePlay` helper it
  // alone uses) lives in `useStagedMaterialization`
  // (state/use-staged-materialization.ts). terra-ohe.7 deleted its former
  // sibling — the client-side send-time materialization function — entirely
  // — see that module's doc comment — so this hook's deps shrank to just
  // what `resetItemToBase` needs.
  const { resetItemToBase } = useStagedMaterialization({
    stagedIdentity,
    runItemOp,
    cachedItem,
    beginItemWriteBarrier,
    endItemWriteBarrier,
    discardAutosave,
    patchItem,
    queryClient,
  });

  /**
   * terra-ohe.7 (F1/DBOS durable-send migration U6) — REPLACES the deleted
   * client-side send-time materialization function. For each of `itemIds`,
   * looks up its group's play key off `groups` (the render-scope read is
   * fine here: this only runs at Send-click time, synchronously, off the
   * current render — unlike the deleted function, there is no debounced
   * write whose staleness would matter), narrows the batch's item ids to
   * their play key, and hands the whole batch to the pure
   * `buildStagedSendSnapshot` (`state/staged-play-config.ts`). No autosave
   * dispatch, no rollback — see that function's doc comment for why none of
   * that exists any more.
   */
  const buildStagedSnapshot = useCallback(
    (itemIds: number[]): StagedSendConfig | undefined => {
      const targets = new Set(itemIds);
      const itemIdsByPlayKey: Record<string, number[]> = {};
      for (const group of groups) {
        if (group.play_type_id == null || group.play_instance_ref == null) continue;
        const ids = group.items.filter(item => targets.has(item.id)).map(item => item.id);
        if (ids.length === 0) continue;
        const key = playConfigKey(group.play_type_id, group.play_instance_ref);
        itemIdsByPlayKey[key] = [...(itemIdsByPlayKey[key] ?? []), ...ids];
      }
      return buildStagedSendSnapshot(stagedIdentity, itemIdsByPlayKey);
    },
    [groups, stagedIdentity],
  );

  /**
   * PATCH .../items/:id — the reassignment seam ("Change play…", and
   * `UnassignedRow`'s first assignment). See
   * `EnrollQueueContextValue.assignPlay` for the contract; the mechanics:
   *
   * Declared HERE, below `discardAutosave`, rather than beside `addContact`
   * where it used to live: D12 Codex round-2 fix #4 needs to drop the item's
   * whole autosave entry, and a `useCallback` dependency array is evaluated
   * during render, so referencing a `const` declared further down would be a
   * TDZ crash rather than a late-bound call. Pure relocation — nothing else
   * about the ordering of this file's callbacks changed.
   */
  const assignPlay = useCallback(
    async (itemId: number, play: PlayOption) => {
      // ── D12 Codex ROUND-3 fix #3 — serialize, then classify ──────────────
      // The `playChanged` classification below reads the query CACHE, which a
      // still-in-flight sibling reassignment for the same item has not updated
      // yet. So a rapid A→B, then (before B lands) →A sequence classified the
      // second call against a cache that still said A — computing a false
      // "same-play no-op" and skipping every side effect a real B→A move
      // needs (clearing `senderTouched`, discarding the stale autosave entry).
      //
      // Wrapping the whole body in `runItemOp` fixes the misclassification
      // structurally rather than by patching the predicate: the second call
      // does not evaluate `playChanged` until the first has fully completed
      // (PATCH + refetch), at which point the cache IS current and the
      // existing predicate is simply correct. Same mechanism as fix #2's
      // reset/reassign ordering — one queue, not two patches.
      await runItemOp(itemId, async () => {
        // ── D12 Codex round-2 fix #5 — is this a REAL move? ──────────────────
        // `AssignedRow` deliberately does not exclude the current play from its
        // reassign picker (U7: picking it is a documented server no-op —
        // `playChanged` is false, so `queue.ts` resets nothing). Round 1's
        // success handler cleared `senderTouched` for ANY successful call
        // though, so re-picking the assigned play silently un-pinned a
        // deliberate manual sender override and put the row back to following
        // the staged global sender — a destructive side effect of an action the
        // UI describes as harmless.
        //
        // The predicate MIRRORS the server's own (`queue.ts`'s `playChanged`):
        // the patch sets a non-null play, the item ALREADY had one (a first
        // assignment is not a reassignment — there is no play-A state to
        // discard, and treating it as one would fight U5's "a stored sender is
        // never overwritten by a play assignment"), and the (type, ref) pair
        // actually differs. An INSTANCE-only move counts, exactly as it does
        // server-side.
        //
        // Read from the CACHE rather than render-scope `groups` — the same
        // rationale `sendMutation.onMutate` documents: `groups` can be stale by
        // the time a click's handler runs. An item absent from the cache
        // entirely (never a rendered row picking its own play, which is the
        // only shape finding #5 is about) is treated as a move: there is no
        // visible pin to lose, and carrying stale old-play state forward is the
        // worse failure of the two.
        //
        // Round 3: evaluated INSIDE the queued op (see above), so "the cache"
        // means "the cache as of our turn", not "as of the click".
        const current = cachedItem(itemId);
        const playChanged = current === undefined
          || (current.play_type_id !== null
            && (current.play_type_id !== play.play_type_id || current.play_instance_ref !== play.play_instance_ref));

        // D12 Codex round-2 fix #4 — bar this item's autosave for the whole
        // reassignment. `AssignedRow` awaits `onFlushSave` before calling this
        // and SWALLOWS its failure by design (the rep is deliberately moving
        // away from that draft), which leaves the failed write folded back into
        // `pendingPatch`. Without the barrier, anything that flushes the item
        // during the reassignment (a concurrent group Send) could push the OLD
        // play's sender/copy onto the row the server is resetting. Claimed only
        // for a real move: a same-play no-op must not gate writes either.
        if (playChanged) beginItemWriteBarrier(itemId);
        try {
          await patchMutation.mutateAsync({
            itemId,
            patch: { play_type_id: play.play_type_id, play_instance_ref: play.play_instance_ref },
          });
          // Browse-overlay auto-open-editor follow-up (2026-08-22) — see
          // `QueueFlashRequest.openEditorAfterAssign`'s doc comment.
          // `patchMutation`'s own `onSuccess` invalidates QUEUE_KEY
          // fire-and-forget (unchanged — most `patchMutation` callers, e.g. the
          // per-keystroke autosave debounce loop, need that FAST resolution),
          // so THIS caller awaits its own extra invalidation, mirroring
          // `addMutation`'s consume-before-mount race fix (2026-08-22, see its
          // own `onSuccess` doc comment above) for the SAME reason: the caller
          // (`UnassignedRow` in QueueBody.tsx) sequences `requestQueueFlash`
          // after `assignPlay`'s own promise resolves, and that's only
          // race-free if the queue cache already reflects the assignment
          // (item moved into its assigned group, `AssignedRow` about to mount)
          // by the time it does. `invalidateQueries` on an already-invalidated
          // key is a safe no-op/dedupe against the fire-and-forget call above,
          // not a second redundant network round trip.
          await queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
          // Fix #5's other half: a same-play pick changes NOTHING about the
          // item, so it must change nothing about the item's client state
          // either. Returning here (rather than guarding each clear below)
          // keeps "no-op" a single, checkable statement.
          if (!playChanged) return;
          // ── D12 Codex round-2 fix #4 — drop the item's ENTIRE entry ─────────
          // The server has just nulled this item's drafts and send-gate
          // overrides (`queue.ts`'s reassignment reset), so EVERY tracked write
          // in the entry describes a play the item is no longer on:
          //
          //  - `pendingPatch` — including one restored by the swallowed flush
          //    failure above. Flushed later, it re-applies play A's copy/sender
          //    onto the item under play B.
          //  - `ackedPatch` — retained until a queue read reflects it, and read
          //    by the (now-deleted) client-side materialization function's
          //    ownership chain. Kept across a reassignment it made the next
          //    Send materialize (or preserve) the old play's values; worse,
          //    the values would never be reflected (the server nulled them),
          //    so the release pass would never clear it and the item stayed
          //    pinned to them for the session.
          //
          // Round 1 cleared only `senderTouched`, which is why both leaks
          // survived it. `discardAutosave` (not a bare map delete) so an
          // already-dispatched write is awaited rather than orphaned mid-flight.
          await discardAutosave(itemId);
          // D12 Codex fix (round 1), finding #7 — the item now belongs to a
          // DIFFERENT play, with its own staged global sender and its own
          // role-derived default. Its `senderTouched` pin ("don't let the play's
          // staged sender follow this row") was made against the OLD play and
          // means nothing here, yet nothing cleared it: the staged-config
          // reconcile pass only prunes ids that left the QUEUE entirely, and a
          // reassigned item never does. So it stayed pinned — permanently opted
          // out of its new play's sender config and permanently wearing an
          // "Overridden" badge it never earned.
          //
          // Cleared AFTER the PATCH resolves, matching `resetItemToBase`'s
          // ordering rule for the same reason: a REJECTED reassign leaves the
          // item on its old play, where the pin is still correct.
          //
          // No per-item sender STAGING to prune alongside it — staged senders
          // are keyed by play (`baseSender[playKey]`), never by item, so the
          // item simply reads its new play's bucket from the next render on.
          clearSenderTouched(stagedIdentity, itemId);
        } finally {
          if (playChanged) endItemWriteBarrier(itemId);
        }
      });
    },
    [runItemOp, cachedItem, patchMutation, queryClient, stagedIdentity, discardAutosave, beginItemWriteBarrier, endItemWriteBarrier],
  );

  /**
   * The staged-config prune (D8 Part B point 4's second half) — staged base
   * copy now PERSISTS, so it has to be pruned rather than abandoned: sent
   * item ids leave `senderTouched`, and a play's staged entries clear once
   * it has no remaining open items ("on send this work is lost" applies to
   * the completed batch, while a PARTIAL send keeps the play staged for the
   * rows still in the queue).
   *
   * Driven off the queue itself rather than bolted onto the send-settle
   * callback, which makes ONE mechanism cover both cases the plan lists: a
   * settled send invalidates `QUEUE_KEY`, so the refetch that drops the sent
   * rows is what triggers the prune, and the same pass reconciles entries
   * orphaned by a send completed in another tab or a previous session (the
   * plan's "runs once against the first queue load after hydration").
   *
   * Gated on the response being WELL-FORMED per the real schema — the same
   * precedent the step-draft ghost sweep below already sets, and for the same
   * reason (Codex adversarial review, D8 regression #5). A truthiness check
   * was NOT enough: `validateResponse` logs a shape mismatch and returns the
   * payload AS-IS (by design — it never throws), so a malformed/degenerate
   * response (e.g. `{}`) arrives here as a truthy `queueData` whose `groups`
   * reads `[]`, and this prune — which IS destructive — then deleted EVERY
   * staged play's config as if every play had been fully sent. A load-in-
   * flight `queueData === undefined` fails the same check, so the original
   * "don't mistake a pending load for an empty queue" guard is preserved.
   * Hygiene + semantics only, never correctness: per-item values beat the
   * staged rung in every derivation, so a stale entry cannot clobber a
   * materialized item.
   */
  useEffect(() => {
    if (!QueueResponseSchema.safeParse(queueData).success) return;
    if (!stagedConfigHasHydrated()) return;
    const openItemIds = new Set<number>();
    const openPlayKeys = new Set<PlayKey>();
    for (const group of groups) {
      for (const item of group.items) openItemIds.add(item.id);
      if (group.play_type_id != null && group.play_instance_ref != null) {
        openPlayKeys.add(playConfigKey(group.play_type_id, group.play_instance_ref));
      }
    }
    reconcileStagedPlayConfig(stagedIdentity, { openItemIds, openPlayKeys });
  }, [queueData, groups, stagedIdentity]);

  /**
   * ── D12 Codex round-3 fix #6 — the draft-reset generation SETTLE pass ─────
   *
   * `resetItemToBase` no longer bumps the per-item draft-reset generation
   * itself. It records the post-reset trio it is waiting for
   * (`requestItemDraftReset`) and THIS effect performs the bump — but only
   * once the item in `groups` actually carries that trio.
   *
   * Being an effect is the entire mechanism, not an implementation detail:
   * effects run after React has COMMITTED a render, so by the time this body
   * executes the editor's parent (and therefore `EnrollmentItemEditor` itself)
   * has already re-rendered with the post-reset `item` prop. Bumping here
   * cannot re-key `useDraftState` ahead of the data it is meant to reseed
   * from, which is exactly what round 2's synchronous bump could do: `await
   * invalidateQueries` proves the CACHE is current and says nothing about
   * React's render, and nothing orders React Query's (batched, microtask)
   * observer notification against the awaiting continuation. A reset that lost
   * that race permanently seeded the new generation from the PRE-reset item —
   * self-corrected for copy by the live-follow effect, never for SENDER, so a
   * stale sender got baked into the draft and the editor's own Send PATCHed it
   * back.
   *
   * Depends on `pendingDraftResets` (a zustand read) as well as `groups`
   * because REGISTRATION happens in an async callback, in a commit where
   * `groups` may not have changed — without the reactive dependency the pass
   * would simply never run for that reset.
   *
   * Comparison goes through `itemReflectsPatch`, the same route-normalization-
   * aware predicate `ackedPatch`'s release pass uses, so a blank draft stored
   * as NULL matches instead of pinning the entry forever. A reset whose trio
   * happens to already match the pre-reset item is the degenerate case and is
   * settled immediately — correctly, since pre- and post-reset data are then
   * identical and there is nothing stale to seed from.
   *
   * Gated on a WELL-FORMED response for the same reason the two sweeps above
   * are: `validateResponse` logs-and-returns a shape-mismatched payload as-is,
   * and a degenerate `{}` reads as an empty queue — which would make every
   * pending reset look like a departed item and drop its reseed silently.
   */
  const pendingDraftResets = usePendingItemDraftResets();
  useEffect(() => {
    const pendingIds = Object.keys(pendingDraftResets);
    if (pendingIds.length === 0) return;
    if (!QueueResponseSchema.safeParse(queueData).success) return;
    const liveItems = new Map(groups.flatMap(g => g.items).map(i => [i.id, i] as const));
    for (const key of pendingIds) {
      const itemId = Number(key);
      const item = liveItems.get(itemId);
      if (item === undefined) {
        // Sent/removed before its post-reset read landed — no mounted editor
        // is left to reseed, so drop the request rather than bump.
        dropItemDraftReset(itemId);
        continue;
      }
      if (!itemReflectsPatch(item, pendingDraftResets[itemId])) continue;
      settleItemDraftReset(itemId);
    }
  }, [pendingDraftResets, queueData, groups]);

  // ── Basic save-and-send (plan 2026-08-19-004) — the step-override draft
  // store's accessors + the ONE write moment. The debounced/serialized/
  // drain-until-idle step-draft machinery that used to live here is GONE:
  // with no background writes, "saved before send" is a plain sequential
  // `await`, not a concurrency protocol. ──────────────────────────────────

  // Typing-lag fix (2026-08-20): a stable helper — reads/writes the
  // step-override store directly, no state — so callers below keep
  // dependency arrays that only carry the (per-mount, therefore stable)
  // store instance, instead of depending on a `clearStepSaveError` whose OWN
  // identity used to change with `setStepSaveErrors`'s state setter closure.
  const clearStepSaveError = useCallback(
    (key: string) => {
      const { saveErrors } = stepOverrideStore.getState();
      if (!saveErrors.has(key)) return;
      const next = new Map(saveErrors);
      next.delete(key);
      stepOverrideStore.setState({ saveErrors: next });
    },
    [stepOverrideStore],
  );

  const editStepOverride = useCallback(
    (itemId: number, stepNumber: number, stepType: string, values: StepOverrideSnapshot) => {
      const key = stepOverrideKey(itemId, stepNumber);
      const { drafts } = stepOverrideStore.getState();
      stepOverrideStore.setState({ drafts: new Map(drafts).set(key, { itemId, stepNumber, stepType, values }) });
      clearStepSaveError(key);
    },
    [stepOverrideStore, clearStepSaveError],
  );

  const resetStepOverrideToDefault = useCallback(
    (itemId: number, stepNumber: number, stepType: string) => {
      const key = stepOverrideKey(itemId, stepNumber);
      // Round-5 adversarial follow-up on Finding #3: the prior fix tried to
      // DETECT whether a persisted row exists (a session ledger OR a query-
      // cache scan) and only recorded a DELETE intent when one of those
      // sources was confident. Codex found both sources can be wrong at
      // once — e.g. a PUT that commits server-side but whose response is
      // lost/times out never updates the ledger, and the cache is equally
      // stale in that same window — silently dropping the entry instead of
      // deleting the just-committed override. There is no cheap detection
      // that closes every such gap. So: stop detecting. ALWAYS record reset
      // as a DELETE intent; Send always issues the DELETE (Decision #4's
      // original intent). A redundant DELETE for a local-only edit that was
      // never persisted is a safe no-op (`deleteMany`, idempotent by
      // contract — src/services/flow/step-drafts.ts) — one occasionally-
      // wasted request beats a staleness-sensitive lookup that can silently
      // leave a real override on the server.
      const next = new Map(stepOverrideStore.getState().drafts);
      next.set(key, { itemId, stepNumber, stepType, values: null });
      stepOverrideStore.setState({ drafts: next });
      clearStepSaveError(key);
    },
    [stepOverrideStore, clearStepSaveError],
  );

  /** See `EnrollQueueContextValue.clearStepOverridesForItem`'s doc comment. */
  const clearStepOverridesForItem = useCallback(
    (itemId: number): (() => void) => {
      const sweep = clearStepOverrideDraftsForItem(stepOverrideStore, itemId);
      return () => restoreStepOverrideDraftsForItem(stepOverrideStore, itemId, sweep);
    },
    [stepOverrideStore],
  );

  /**
   * Typing-lag fix (2026-08-20): referentially STABLE (its only dep is the
   * per-mount store instance — it reads `getState()` directly, never React
   * state), unlike the old version which depended on `stepOverrideDrafts`
   * state and therefore changed identity on every edit. Being a member of the
   * `value` memo below, that identity churn was the actual mechanism of the
   * bug (see `state/step-override-drafts.ts`'s header) — this function itself
   * no longer causes ANY re-render; a consumer that needs to react to changes
   * pairs it with `subscribeStepOverrides` (`StepOverrideRow`,
   * StepOverrideEditor.tsx, is the one that does, via
   * `useStepOverrideEntry`).
   */
  const stepOverrideDraftFor = useCallback(
    (itemId: number, stepNumber: number): StepOverrideDraft | null => {
      const key = stepOverrideKey(itemId, stepNumber);
      const { drafts, acked } = stepOverrideStore.getState();
      // U2 (gong-bulk-step-defaults-bugfix plan) — the `acked` fallback rung
      // closes the post-ack/pre-refetch window `saveStepOverrides`' success
      // block used to leave open by deleting the entry outright: an
      // acked-but-not-yet-reflected write still counts as "this row is
      // overridden" for BOTH the display rung (`StepOverrideRow`'s
      // `localDraftFor`) and the materialization guard
      // (`deriveStagedStepWrites`' `localDraftFor`, bound to this same
      // accessor). Mirrors `ackedPatch`'s role for the base-copy autosave
      // rail.
      return drafts.get(key) ?? acked.get(key) ?? null;
    },
    [stepOverrideStore],
  );

  /** Same stability rationale as `stepOverrideDraftFor` above. */
  const stepSaveErrorFor = useCallback(
    (itemId: number, stepNumber: number): string | null =>
      stepOverrideStore.getState().saveErrors.get(stepOverrideKey(itemId, stepNumber)) ?? null,
    [stepOverrideStore],
  );

  /**
   * Typing-lag fix (2026-08-20) — the subscribe half of the pair
   * `StepOverrideRow` (StepOverrideEditor.tsx) uses to read its own
   * `${itemId}:${stepNumber}` entry reactively without going through the
   * context/props chain. Referentially stable (only dep: the per-mount store
   * instance). Fires on either a draft OR a save-error change for the SAME
   * key and on NOTHING else — `subscribeStepOverrideKey` filters zustand's
   * store-wide notification down to that one key — so a row's
   * `localDraftFor`/`saveErrorFor` read both off one subscription and a
   * sibling step's edit never re-renders this row.
   */
  const subscribeStepOverrides = useCallback(
    (itemId: number, stepNumber: number, onChange: () => void): (() => void) =>
      subscribeStepOverrideKey(stepOverrideStore, stepOverrideKey(itemId, stepNumber), onChange),
    [stepOverrideStore],
  );

  // P1-LOOP's identity lesson, applied to the two step mutations too: depend
  // on the constructor-bound `mutateAsync` (stable), never on the mutation
  // result object (new identity every render).
  const { mutateAsync: putStepOverrideAsync } = stepDraftMutation;
  const { mutateAsync: deleteStepOverrideAsync } = clearStepDraftMutation;

  /**
   * See the `EnrollQueueContextValue.saveStepOverrides` doc comment — the ONE
   * write moment (Decision #5: parallel, all-settled, reject only after
   * everything settles).
   *
   * Plan 2026-09-08-002 Part 2, v3 rewrite, Key Technical Decision 5 — this
   * is ALSO the one materialization point for batch step defaults staged in
   * `state/staged-play-config.ts`'s `stepCopy` field (the step-level
   * counterpart of `stageBaseCopyForSend`, chosen specifically because it
   * survives the in-flight DBOS durable-send migration — that migration's
   * own plan keeps "flush → save step overrides → POST" as the surviving
   * per-item pipeline; see the plan's Scope Boundaries section). The
   * preamble below runs BEFORE the existing entries read: a pure follower
   * has zero own entries at that point, and the preamble is what creates
   * them, by writing ordinary local drafts through `editStepOverride` — the
   * very next lines of this function then pick those up and PUT them like
   * any other edit. No card-level wrapper needed: every send path (group
   * pipeline, row `handleSend`, the standalone step-save path) already
   * calls `saveStepOverrides(itemId)`.
   */
  const saveStepOverrides = useCallback(
    async (itemId: number) => {
      // Cache miss (an item the queue snapshot doesn't know about) skips
      // materialization — the send proceeds with flow defaults, same as
      // nothing staged. `play_type_id`/`play_instance_ref` are nullable on
      // `EnrollmentItem` (an unassigned item); no play key, no staged steps.
      const item = cachedItem(itemId);
      if (item && item.play_type_id !== null && item.play_instance_ref !== null) {
        const playKey = playConfigKey(item.play_type_id, item.play_instance_ref);
        const stagedSteps = readStagedConfig(stagedIdentity).stepCopy[playKey];
        // U3 (gong-bulk-step-defaults-bugfix plan, MUST-ADDRESS) — resolve
        // the flow's LIVE steps before materializing, so a staged entry for
        // a step Gong-side deleted/renumbered upstream never plans a PUT the
        // send-time guard would reject. `item.play_instance_ref` IS the flow
        // id for `gong_flow` (`stepCopy` entries only ever exist for gong
        // plays — only `BulkStepDefaultsEditor` writes them). Fired ONLY
        // when something is actually staged for this play, so an ordinary
        // save with nothing staged gains no extra network call. `fetchQuery`
        // shares `useGongFlowStepsForced`'s exact key
        // (`[...FLOW_GONG_STEPS_KEY, flowId, 'forced']`), so this is a cache
        // hit whenever any editor for this flow already mounted this
        // session — deduped + cached on miss (`staleTime: Infinity`).
        // `try/catch` → `null`: a failed resolution degrades to "batch not
        // applied", never a blocked send (see `deriveStagedStepWrites`'s own
        // `liveSteps: null` fail-safe).
        let liveSteps: ReadonlyArray<{ step_order: number; step_type: string }> | null = null;
        if (stagedSteps && Object.keys(stagedSteps).length > 0) {
          try {
            const flowId = item.play_instance_ref;
            const stepsResponse = await queryClient.fetchQuery({
              queryKey: [...FLOW_GONG_STEPS_KEY, flowId, 'forced'],
              queryFn: () => fetchGongFlowSteps(apiFetch, flowId),
              staleTime: Infinity,
            });
            liveSteps = stepsResponse.steps;
          } catch {
            liveSteps = null;
          }
        }
        const writes = deriveStagedStepWrites({
          item,
          stagedSteps,
          localDraftFor: stepNumber => stepOverrideDraftFor(itemId, stepNumber),
          liveSteps,
        });
        for (const write of writes) {
          editStepOverride(itemId, write.stepNumber, write.stepType, write.values);
        }
      }

      // Read through the STORE (always-current truth), never a render
      // closure's state snapshot — see the store module's header. The item
      // prefix includes the colon, which keeps item 1 off item 11's keys.
      const prefix = stepOverrideItemPrefix(itemId);
      const entries = Array.from(stepOverrideStore.getState().drafts.entries()).filter(([key]) => key.startsWith(prefix));
      if (entries.length === 0) return;
      const results = await Promise.allSettled(
        entries.map(([, draft]) =>
          draft.values === null
            ? deleteStepOverrideAsync({ itemId: draft.itemId, stepNumber: draft.stepNumber })
            : putStepOverrideAsync({
                itemId: draft.itemId,
                stepNumber: draft.stepNumber,
                stepType: draft.stepType,
                // Codex #4 (round-5 adversarial follow-up): `draft.values`
                // is the row's FULL effective snapshot, which for a
                // non-email step type always carries `subject_draft: ''`
                // (there's no Subject field to edit — see StepOverrideEditor's
                // `hasSubject`). Sending that '' verbatim stores a real,
                // non-null `subjectDraft: ''` override, and the Gong sender
                // (`enrollment-sender.ts`) only treats `null` as "no
                // override" — so it would ship an explicit empty subject to
                // Gong for a step type whose own schema documents it as
                // body-only. Drop the key entirely for non-email steps so
                // the PUT's `'subject_draft' in body` partial-upsert check
                // (flow-queue.ts) leaves any stored value untouched instead
                // of overwriting it with ''.
                patch: EMAIL_STEP_TYPES.has(draft.stepType as GongFlowStepDetailWire['step_type'])
                  ? draft.values
                  : { body_draft: draft.values.body_draft },
              }),
        ),
      );
      const failuresByKey = new Map<string, unknown>();
      results.forEach((result, i) => {
        if (result.status === 'rejected') failuresByKey.set(entries[i][0], result.reason);
      });
      if (failuresByKey.size > 0) {
        // Keep ALL entries (retry = click Send again; re-PUTting an
        // already-saved snapshot is idempotent). Record per-step errors;
        // clear a succeeded sibling's stale error.
        const nextErrors = new Map(stepOverrideStore.getState().saveErrors);
        for (const [key] of entries) {
          const reason = failuresByKey.get(key);
          if (reason !== undefined) {
            nextErrors.set(key, asFlushError(reason).message);
          } else {
            nextErrors.delete(key);
          }
        }
        stepOverrideStore.setState({ saveErrors: nextErrors });
        // Reject only now — after EVERY write settled (allSettled above):
        // the caller's lock must not release while a sibling is in flight.
        throw asFlushError(failuresByKey.values().next().value);
      }
      // Full success (U2, gong-bulk-step-defaults-bugfix plan) — MOVE the
      // saved entries into `acked` rather than deleting them outright:
      // identity-guarded, so an edit that landed DURING the save phase (a
      // fresh draft object under the same key) keeps its fresh draft and is
      // never acked (the fresh draft already protects the row on its own).
      // The mutations' own QUEUE_KEY invalidation repaints rows from
      // `item.step_drafts`, but that invalidation is fire-and-forget — the
      // retention closes the exact window between this ack and that
      // refetch landing, released by the queue-driven sweep effect below
      // (`stepDraftReflectsAckedDraft`), mirroring `ackedPatch`.
      const nextDrafts = new Map(stepOverrideStore.getState().drafts);
      const nextAcked = new Map(stepOverrideStore.getState().acked);
      for (const [key, draft] of entries) {
        if (nextDrafts.get(key) === draft) {
          nextDrafts.delete(key);
          nextAcked.set(key, draft);
        }
      }
      stepOverrideStore.setState({ drafts: nextDrafts, acked: nextAcked });
      const prevErrors = stepOverrideStore.getState().saveErrors;
      if (entries.some(([key]) => prevErrors.has(key))) {
        const clearedErrors = new Map(prevErrors);
        for (const [key] of entries) clearedErrors.delete(key);
        stepOverrideStore.setState({ saveErrors: clearedErrors });
      }
    },
    [stepOverrideStore, putStepOverrideAsync, deleteStepOverrideAsync, cachedItem, stagedIdentity, editStepOverride, stepOverrideDraftFor, queryClient, apiFetch],
  );

  /**
   * F9 god-context cut 4 of 4 — the UI-signal layer (sidebar open state,
   * tab + unseen-sends blip, the By Play card landing, contact-jump and queue-flash signals,
   * Requests-tab selection/search seed) and the thin call wrappers around
   * the mutations now live in `useQueueUiSignals`.
   *
   * The mutation objects themselves stay in THIS file (reserved for the
   * F1/DBOS worktree) and are handed to the hook by reference. The hook is
   * therefore called AFTER them, which means `sendMutation`'s deferred
   * `onSuccess` reaches `setIsOpen`/`setActiveTab`/
   * `setSendSkipReasons` as forward references — the same shape the cut
   * already used. Every one of them is read only from inside a callback that
   * runs after render, never during it.
   */
  const {
    isOpen, open, close, toggle,
    activeTab, setActiveTab, rightOwner, openQueueTab, openChat, openCustomInvite, openNotes,
    queueFlashRequest, consumeQueueFlashRequest, requestQueueFlash,
    selectedRequestId, openRequestDetail, closeRequestDetail,
    requestsSearchSeed, openRequestsForContact,
    contactJumpRequest, jumpToContact, registerContactJumpHandler,
    queuePlayLanding, requestPlayCardLanding, dismissQueuePlayLanding,
    selectInputOption, refreshInputOptionsIfStale, removeItem, unstickItem, sendItems, sendSkipLabelFor,
    setSendSkipReasons, clearSendSkipLabel, setIsOpen,
  } = useQueueUiSignals({ patchItem, removeMutation, unstickMutation, sendMutation, runItemOp, invalidateContactState });

  /**
   * terra-ohe.8 (F1/DBOS durable-send migration U6) — `POST
   * .../items/:id/cancel`. Unlike `unstickItem`'s wrapper (which swallows an
   * EXPECTED 400 silently), every rejection here is surfaced: a cancel 400
   * means the row already moved past cancelable (see `cancelMutation`'s doc
   * comment) and the rep should know that attempt didn't land. Kept local to
   * this file (not folded into `useQueueUiSignals`) because it needs direct
   * access to `pendingSendIdsRef`, which is provider-scoped send-poll state.
   */
  const cancelQueuedItem = useCallback(
    async (itemId: number) => {
      try {
        await cancelMutation.mutateAsync(itemId);
      } catch (err) {
        // apiFetch already announced a refusal.
        if (err instanceof WriteRefusedError) return;
        toast.error(err instanceof PatchQueueItemError ? err.message : "Couldn't cancel the item — try again");
      }
    },
    [cancelMutation],
  );

  // F9 god-context cut 5 — the bulk add/remove mutations and their thin call
  // wrappers now live in `useBulkQueueMutations` (state/use-bulk-queue-mutations.ts).
  // Called AFTER `useQueueUiSignals` above, at the position `bulkAdd`/`bulkRemove`
  // used to occupy: both wrappers need `requestPlayCardLanding`/`openQueueTab`,
  // which that hook produces.
  const { bulkAdd, bulkRemove } = useBulkQueueMutations({
    queryClient,
    invalidateContactState,
    requestPlayCardLanding,
    openQueueTab,
  });

  // Ghost-entry sweep (plan 2026-08-19-004, Decision #9): drop local step
  // drafts (and their errors) whose item no longer exists in the queue —
  // sent/removed items must not hold the beforeunload guard below hostage.
  // Both maps share the `${itemId}:${stepNumber}` key shape, so one sweeper
  // serves both. A loading/absent response sweeps nothing.
  useEffect(() => {
    // Finding #4 (adversarial follow-up, round 5 hardening): gate on the
    // response being WELL-FORMED per the actual schema, not a shallow
    // `Array.isArray` check. `validateResponse` logs-and-returns a shape-
    // mismatched payload AS-IS (by design — it never throws), so a payload
    // that merely LOOKS array-shaped at the top level (e.g. genuinely empty
    // `items` from a truncated/buggy response) can pass a superficial check
    // while still being wrong — and once wrong, this sweep is destructive
    // (it deletes local drafts). Re-validate independently against the real
    // schema here so a malformed response — however shallow-valid it looks
    // — sweeps NOTHING rather than trusting whatever slipped past the
    // query's own observational validation.
    if (!QueueResponseSchema.safeParse(queueData).success) return;
    // A Map (was a Set of ids) — the regression #2 release pass below needs
    // each live item's actual fields, and the step sweep only ever asks
    // "is this id still here?", which `.has` answers identically.
    const liveItems = new Map(groups.flatMap(g => g.items).map(i => [i.id, i] as const));
    // ── D8 regression #2, release half ──────────────────────────────────
    // `ackedPatch` (see `DebouncedWriteEntry`) keeps an acknowledged autosave
    // visible through the post-ack/pre-refetch window (originally so the now-
    // deleted client-side materialization function wouldn't clobber it).
    // THIS is where that retention ends: the read has landed, so an
    // item whose server-known fields now match the acked patch no longer
    // needs protecting, and an entry with nothing else outstanding goes away
    // — restoring the map's "an entry exists iff something is outstanding"
    // invariant. Lives in this effect rather than its own because it needs
    // exactly the same three guards the step sweep below already establishes
    // (schema-validated response, `groups`, and the `busyItems` pipeline
    // lock), and reconciling local write-state against the queue read is one
    // job, not two.
    for (const [itemId, entry] of autosaveEntriesRef.current) {
      if (entry.ackedPatch === null) continue;
      const item = liveItems.get(itemId);
      const settled = entry.timer === null && entry.inFlight === null && entry.pendingPatch === null;
      if (item === undefined) {
        // Left the queue (sent/removed) — nothing left to protect. Never for
        // an item mid-pipeline: its save writes are what this entry IS.
        if (settled && !busyItems.has(itemId)) autosaveEntriesRef.current.delete(itemId);
        continue;
      }
      if (!itemReflectsPatch(item, entry.ackedPatch.patch)) continue;
      entry.ackedPatch = null;
      if (settled && entry.lastError == null) autosaveEntriesRef.current.delete(itemId);
    }
    // ── U2 (gong-bulk-step-defaults-bugfix plan), acked release pass ──────
    // The step-override store's OWN `acked` map (see `saveStepOverrides`'
    // success block above), released the same way `ackedPatch` is: once a
    // queue read reflects the write, retention ends. Departed-item cleanup
    // for `acked` is handled by the generic `sweepDeparted` extension below
    // — this pass only handles the "still-open, now-reflected" release, via
    // the pure `stepDraftReflectsAckedDraft` predicate.
    const ackedNow = stepOverrideStore.getState().acked;
    if (ackedNow.size > 0) {
      let nextAcked: Map<string, StepOverrideDraft> | null = null;
      for (const [key, ackedDraft] of ackedNow) {
        const itemId = Number(key.slice(0, key.indexOf(':')));
        const item = liveItems.get(itemId);
        if (item === undefined) continue; // handled by sweepDeparted below
        const isEmailStep = EMAIL_STEP_TYPES.has(ackedDraft.stepType as GongFlowStepDetailWire['step_type']);
        if (!stepDraftReflectsAckedDraft(item.step_drafts, ackedDraft, isEmailStep)) continue;
        if (!nextAcked) nextAcked = new Map(ackedNow);
        nextAcked.delete(key);
      }
      if (nextAcked) stepOverrideStore.setState({ acked: nextAcked });
    }
    // Finding #4, second half: an item inside an active send pipeline is
    // never "departed", regardless of what this response says — its save
    // writes are the very thing the entries describe. Read from `busyItems`
    // (state; also this effect's re-run trigger) so releasing a lock
    // re-evaluates the sweep for anything that departed while locked.
    const departed = (key: string): boolean => {
      const itemId = Number(key.slice(0, key.indexOf(':')));
      return !busyItems.has(itemId) && !liveItems.has(itemId);
    };
    // Returns [next, sweptCount] rather than mutating in place: the Maps are
    // copy-on-write (see the store module's header), and the count is what
    // decides whether to write at all — a no-op `setState` would notify every
    // subscriber for nothing. Which keys changed no longer has to be tracked
    // by hand: `subscribeStepOverrideKey` derives that per subscriber from
    // zustand's own (state, previous) pair.
    function sweepDeparted<T>(prev: ReadonlyMap<string, T>): [Map<string, T>, number] {
      const next = new Map(prev);
      for (const key of prev.keys()) {
        if (departed(key)) next.delete(key);
      }
      return [next, prev.size - next.size];
    }
    const [sweptDrafts, sweptDraftCount] = sweepDeparted(stepOverrideStore.getState().drafts);
    if (sweptDraftCount > 0) stepOverrideStore.setState({ drafts: sweptDrafts });
    const [sweptErrors, sweptErrorCount] = sweepDeparted(stepOverrideStore.getState().saveErrors);
    if (sweptErrorCount > 0) stepOverrideStore.setState({ saveErrors: sweptErrors });
    // U2 — `acked` gets the same departed-item cleanup drafts/saveErrors do
    // (reads the state AFTER the release pass above, so a just-released key
    // is never double-counted).
    const [sweptAcked, sweptAckedCount] = sweepDeparted(stepOverrideStore.getState().acked);
    if (sweptAckedCount > 0) stepOverrideStore.setState({ acked: sweptAcked });
  }, [stepOverrideStore, queueData, groups, busyItems, autosaveEntriesRef]);

  // Navigation-guard history (do not silently reverse): the queue-WIDE
  // beforeunload guard was removed 2026-07-22 (Spencer QA: it blocked closing
  // ANY localhost tab while the queue had items) and STAYS dead — the queue
  // is DB-backed, so a populated queue alone loses nothing on tab close. What
  // changed (plan 2026-08-19-004, D3): Gong step overrides are now
  // deliberately unsaved-until-Send state — potentially a rep's entire
  // rewritten email sequence — and the dirty-state plumbing that 2026-07-22
  // note priced out now exists for free: the draft store's size. So the
  // guard is SCOPED to exactly that. No in-app router blocker: this provider
  // mounts once above the router and never unmounts on in-app navigation, so
  // only tab close/refresh/hard navigation can lose a draft — precisely what
  // beforeunload covers.
  // Typing-lag fix (2026-08-20): the actual `useUnsavedChangesWarning` call
  // lives in `StepOverrideUnsavedGuard` below, mounted as a Provider
  // SIBLING (never inline in this component's own render) — see that
  // component's doc comment for why: subscribing to the store's "any
  // unsaved?" flag HERE, in the main provider body, would re-render this
  // whole component on every unsaved-flag transition, and this file has
  // several OTHER pre-existing unstable `useCallback`s (`addContact`/
  // `assignPlay`/`sendersFor`/etc., which depend on non-memoized `useMutation`/
  // `useQueries` result objects — a separate, pre-existing perf gap, out of
  // this fix's scope) that would recompute the `value` memo on ANY provider
  // render regardless. Isolating the subscription into its own leaf
  // component keeps step-override activity from ever touching this
  // component's render at all.

  const value = useMemo<EnrollQueueContextValue>(() => ({
    groups,
    plays,
    activePlayTypes,
    gongFlowsSuggested,
    sendersFor,
    defaultSenderFor,
    inputOptionsFor,
    totalCount,
    isOpen,
    open,
    close,
    toggle,
    activeTab,
    setActiveTab,
    rightOwner,
    openQueueTab,
    openChat,
    openCustomInvite,
    openNotes,
    queueFlashRequest,
    consumeQueueFlashRequest,
    requestQueueFlash,
    selectedRequestId,
    openRequestDetail,
    closeRequestDetail,
    requestsSearchSeed,
    openRequestsForContact,
    contactJumpRequest,
    jumpToContact,
    registerContactJumpHandler,
    queuePlayLanding,
    requestPlayCardLanding,
    dismissQueuePlayLanding,
    baseCopyDraftStore,
    buildStagedSnapshot,
    bulkAdd,
    bulkRemove,
    clearStepOverridesForItem,
    readOnly,
    isDemo,
    sendReadOnly,
    readOnlyReason: null,
    isLoading,
    playsResolved,
    isSending,
    addContact,
    assignPlay,
    patchItem,
    autosavePatch,
    flushAutosave,
    discardAutosave,
    resetItemToBase,
    selectInputOption,
    refreshInputOptionsIfStale,
    removeItem,
    unstickItem,
    isUnstickPending: unstickMutation.isPending,
    cancelQueuedItem,
    isCancelPending: cancelMutation.isPending,
    sendItems,
    sendSkipLabelFor,
    editStepOverride,
    resetStepOverrideToDefault,
    stepOverrideDraftFor,
    stepSaveErrorFor,
    subscribeStepOverrides,
    saveStepOverrides,
    isItemBusy,
    beginSendPipeline,
    endSendPipeline,
    runItemOp,
    awaitItemOps,
  }), [
    groups, plays, activePlayTypes, gongFlowsSuggested, sendersFor, defaultSenderFor, inputOptionsFor, totalCount, isOpen, open, close, toggle,
    activeTab, setActiveTab, rightOwner, openQueueTab, openChat, openCustomInvite, openNotes,
    queueFlashRequest, consumeQueueFlashRequest, requestQueueFlash, selectedRequestId, openRequestDetail, closeRequestDetail,
    requestsSearchSeed, openRequestsForContact,
    contactJumpRequest, jumpToContact, registerContactJumpHandler,
    queuePlayLanding, requestPlayCardLanding, dismissQueuePlayLanding,
    baseCopyDraftStore, buildStagedSnapshot, bulkAdd, bulkRemove,
    readOnly, isDemo, sendReadOnly, isLoading, playsResolved, isSending, addContact, assignPlay, patchItem, autosavePatch, flushAutosave, discardAutosave, resetItemToBase, selectInputOption, refreshInputOptionsIfStale, removeItem, unstickItem, unstickMutation.isPending, cancelQueuedItem, cancelMutation.isPending, sendItems, sendSkipLabelFor,
    editStepOverride, resetStepOverrideToDefault, clearStepOverridesForItem, stepOverrideDraftFor, stepSaveErrorFor, subscribeStepOverrides, saveStepOverrides,
    isItemBusy, beginSendPipeline, endSendPipeline, runItemOp, awaitItemOps,
  ]);

  return (
    <EnrollQueueContext.Provider value={value}>
      {children}
      <StepOverrideUnsavedGuard store={stepOverrideStore} />
    </EnrollQueueContext.Provider>
  );
}

export function useEnrollQueue(): EnrollQueueContextValue {
  const ctx = useContext(EnrollQueueContext);
  if (!ctx) throw new Error('useEnrollQueue must be used within an EnrollQueueProvider');
  return ctx;
}

/** The queue context, or null outside a provider — for a leaf that must render in tests/mounts without one. */
export function useOptionalEnrollQueue(): EnrollQueueContextValue | null {
  return useContext(EnrollQueueContext);
}

/** Re-provides the queue with writes disabled for one subtree (Signals "View as"). Outside any provider (unit tests) it renders children unchanged. */
export function EnrollQueueReadOnlyScope({ reason, children }: { reason: string; children: ReactNode }) {
  const parent = useContext(EnrollQueueContext);
  const value = useMemo(() => (parent ? { ...parent, readOnly: true, sendReadOnly: true, readOnlyReason: reason } : null), [parent, reason]);
  if (!value) return <>{children}</>;
  return <EnrollQueueContext.Provider value={value}>{children}</EnrollQueueContext.Provider>;
}
